import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [userType, setUserType] = useState(localStorage.getItem('userType'));
  const [adminRole, setAdminRole] = useState(localStorage.getItem('adminRole'));
  const [loading, setLoading] = useState(true);

  const fetchUser = useCallback(async () => {
    if (token) {
      try {
        const storedUserType = localStorage.getItem('userType');
        // For admin/manager/auditor, fetch from admin endpoint
        if (['admin', 'manager', 'auditor'].includes(storedUserType)) {
          const response = await axios.get(`${API}/admin/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
          setUserType(storedUserType);
          setAdminRole(response.data.role);
          localStorage.setItem('adminRole', response.data.role);
          return response.data;
        } else {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
          setUserType(response.data.user_type);
          return response.data;
        }
      } catch (error) {
        console.error('Auth error:', error);
        logout();
        return null;
      }
    }
    return null;
  }, [token]);

  useEffect(() => {
    const initAuth = async () => {
      await fetchUser();
      setLoading(false);
    };
    initAuth();
  }, [fetchUser]);

  const refreshUser = async () => {
    return await fetchUser();
  };

  const login = async (email, password, isAdmin = false) => {
    const endpoint = isAdmin ? `${API}/admin/auth/login` : `${API}/auth/login`;
    const response = await axios.post(endpoint, { email, password });
    const { access_token, user_type, role, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    localStorage.setItem('userType', user_type);
    if (role) {
      localStorage.setItem('adminRole', role);
      setAdminRole(role);
    }
    setToken(access_token);
    setUserType(user_type);
    setUser(userData);
    
    return response.data;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API}/auth/register`, userData);
    return response.data;
  };

  const registerWithDocuments = async (formData) => {
    const data = new FormData();
    data.append('name', formData.name);
    data.append('email', formData.email);
    data.append('password', formData.password);
    data.append('paypal_email', formData.paypal_email);
    data.append('phone', formData.phone);
    data.append('address', formData.address);
    data.append('ssn_ein', formData.ssn_ein);
    data.append('agree_terms', formData.agree_terms);
    data.append('agree_contractor', formData.agree_contractor);
    data.append('selfie', formData.selfie);
    data.append('id_document', formData.id_document);
    
    const response = await axios.post(`${API}/auth/register-with-documents`, data, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  };

  const setupAdmin = async (adminData) => {
    const response = await axios.post(`${API}/admin/auth/setup`, adminData);
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userType');
    localStorage.removeItem('adminRole');
    setToken(null);
    setUser(null);
    setUserType(null);
    setAdminRole(null);
  };

  const getAuthHeader = () => ({
    headers: { Authorization: `Bearer ${token}` }
  });

  return (
    <AuthContext.Provider value={{
      user,
      token,
      userType,
      adminRole,
      loading,
      login,
      register,
      registerWithDocuments,
      setupAdmin,
      logout,
      getAuthHeader,
      refreshUser,
      isAuthenticated: !!token,
      isAdmin: userType === 'admin',
      isManager: userType === 'manager',
      isAuditor: userType === 'auditor',
      isStaff: ['admin', 'manager', 'auditor'].includes(userType),
      canAccessJobs: ['admin', 'manager'].includes(userType),
      canAccessPayments: ['admin', 'manager', 'auditor'].includes(userType),
      canAccessPayouts: userType === 'admin',
      // Profit & Loss reporting stays admin/auditor only.
      canAccessReporting: ['admin', 'auditor'].includes(userType),
      // Client Reports page (manual report builder) is open to all 3 staff
      // roles — managers need it to write reports for jobs they manage.
      canAccessClientReports: ['admin', 'manager'].includes(userType),
      canAccessStaff: userType === 'admin'
    }}>
      {children}
    </AuthContext.Provider>
  );
};
